import { Box, TextField, Typography, useTheme, TableRow, TableCell, Theme, Skeleton } from "@mui/material"
import { BlackDownloadIcon, SearchIcon } from "../../../assets/SvgIcons"
import CustomTable from "../../investorOverview/common/customTable"
import { AmcTableBody, AmctableHeaderData, InvestorLevelAmcTableBody, InvestorLevelAmctableHeaderData } from "../../investorOverview/mutualFund/common/Table"
import { Order, SortingObj } from "../../investorOverview/types"
import { useSelector } from "react-redux"
import { RootStateType } from "../../../redux-store/reducers"
import { formatCurrency } from "../../../utils/utilityFunctions"
import { getValueColor } from "../../../utils/utilityFunctions"

type InvestorLevelAMCProps = {
    title: string
    tableData: any
    isLoading: boolean
    filters?: boolean
    handleTableSorting: (orderBy: string, order: Order) => void
    sortingObj: SortingObj,
    setSortingObj: ({ order, orderBy }: SortingObj) => void
    maskedData?: boolean
}


export default function InvestorLevelAMCPerformanceAnalysis(props: InvestorLevelAMCProps): JSX.Element {
    const { title, tableData, isLoading, filters = true, handleTableSorting, sortingObj, setSortingObj, maskedData } = props
    const { palette, typography } = useTheme()


    return (
        <Box sx={{
            padding: { xs: '25px 15px 25px 15px', md: '25px 30px 45px 30px' },
            background: palette.common.white,
            borderRadius: '20px'
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h2" color={palette.text.primary}>
                    {title}
                </Typography>
                {filters && (
                    <Box display={'flex'} gap={'10px'}>
                        <Box component={'button'} sx={{ cursor: 'pointer', border: 'none', display: 'flex', gap: '10px', background: 'transparent', alignItems: 'center' }}>
                            <BlackDownloadIcon />
                            <Typography color={palette.text.primary} sx={{ ...typography.body_500 }}>Download</Typography>
                        </Box>
                        <Box component={'div'}
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: '10px',
                                alignItems: 'center',
                                padding: '10px',
                                border: `1px solid ${palette.text.secondary}B2`,
                                borderRadius: '50px'
                            }}>
                            <SearchIcon />
                            <TextField
                                InputProps={{ disableUnderline: true }}
                                placeholder='Search'
                                variant='standard'
                            />
                        </Box>
                    </Box>

                )}

            </Box>
            <Box mt={'20px'}>
                <CustomTable
                    loadingRowsNum={5}
                    isLoading={isLoading}
                    tableBody={
                        <InvestorLevelAmcTableBody
                            tableBodyData={tableData || []}
                            handleCheckboxClick={(id: number) => console.log("Checkbox clicked", id)}
                            maskedData={maskedData}
                        />
                    }
                    tableData={tableData || []}
                    tableBodyLoaderRowCustomStyles={{
                        '& td:first-of-type': {
                            paddingLeft: "0px"
                        },
                    }}
                    tableHeaderData={InvestorLevelAmctableHeaderData}
                    tableHeadRowCustomStyles={{
                        '& th:first-of-type': {
                            paddingLeft: "0px"
                        }
                    }}
                    handleSort={handleTableSorting}
                    tableBodyCustomStyles={{
                        '& tr:last-child td': {
                            border: 0
                        }
                    }}
                    order={sortingObj.order}
                    orderBy={sortingObj.orderBy}
                    tableDataCount={tableData?.length || 0}
                    isPaginationVisible={false}
                    manualResponsiveBody={true}
                    hideResponsiveHeader={true}
                    ManualResponsiveBodyComponent={
                        <MobileResponsiveBodyAmcInves
                            // selectedHeaderKey={sortingObj.orderBy}
                            data={tableData || []}
                            maskedData={maskedData}
                            
                        />
                    }
                />
            </Box>
        </Box>
    )
}

// Custom Mobile Responsive Body Component for Investor Level AMC Performance Analysis
// function MobileResponsiveBody({ selectedHeaderKey, data }: { selectedHeaderKey: string, data: any[] }): JSX.Element {
//     const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

//     function calculatePercentage(x: string | number, y: string | number): string {
//         x = Number(x);
//         y = Number(y);

//         if (!Number.isFinite(x) || !Number.isFinite(y) || y === 0) {
//             return "-";
//         }

//         return `${Math.floor((x / y) * 100)}%`;
//     }

//     const getFormattedValue = (item: any, key: string) => {
//         switch (key) {
//             case 'holding':
//                 return item?.holding ? `${(item?.holding).toFixed(2)}%` : "-";
//             case 'equity':
//                 return item?.equity ? `${(item?.equity).toFixed(2)}%` : "-";
//             case 'debt':
//                 return item?.debt ? `${(item?.debt).toFixed(2)}%` : "-";
//             case 'hybrid':
//                 return item?.hybrid ? `${(item?.hybrid).toFixed(2)}%` : "-";
//             case 'investedValue':
//                 return item?.investedValue < 0 ?
//                     `- ${formatCurrency({ value: Math.abs(item?.investedValue), locale, currencyCode })}` :
//                     `${formatCurrency({ value: item?.investedValue, locale, currencyCode })}`;
//             case 'marketValue':
//                 return item?.marketValue < 0 ?
//                     `- ${formatCurrency({ value: Math.abs(item?.marketValue), locale, currencyCode })}` :
//                     `${formatCurrency({ value: item?.marketValue, locale, currencyCode })}`;
//             case 'unRelease': {
//                 const unRealisedGainLossPerc = calculatePercentage(item?.unRelease, item?.investedValue);
//                 return `${item.unRelease < 0 ? "-" : ""}${formatCurrency({
//                     value: Math.abs(item.unRelease),
//                     locale,
//                     currencyCode,
//                 })} (${unRealisedGainLossPerc})`;
//             }
//             case 'release': {
//                 const realizedGainLossPerc = calculatePercentage(item?.release, item?.investedValue);
//                 return `${item.release < 0 ? "-" : ""}${formatCurrency({
//                     value: Math.abs(item.release),
//                     locale,
//                     currencyCode,
//                 })} (${realizedGainLossPerc})`;
//             }
//             case 'dividend':
//                 return item?.dividend < 0 ?
//                     `- ${formatCurrency({ value: Math.abs(item?.dividend), locale, currencyCode })}` :
//                     `${formatCurrency({ value: item?.dividend, locale, currencyCode })}`;
//             case 'irr':
//                 return item?.irr ? `${(item?.irr).toFixed(2)}%` : "-";
//             default:
//                 return item?.[key] ?? "-";
//         }
//     };

//     return (
//         <>
//             {data.map((item: any, index: number) => (
//                 <TableRow key={index}>
//                     <TableCell align="left">
//                         <Typography
//                             variant="body_500"
//                             sx={(theme: Theme) => ({
//                                 color: theme.palette.text.primary,
//                                 '&:hover': {
//                                     color: theme.palette.primary[500],
//                                 },
//                                 fontSize: '16px',
//                             })}
//                         >
//                             {item?.amcname}
//                         </Typography>
//                     </TableCell>
//                     <TableCell align="center">
//                         <Typography
//                             variant="body_500"
//                             sx={(theme: Theme) => ({
//                                 color: theme.palette.text.primary,
//                                 fontSize: '16px',
//                             })}
//                         >
//                             {getFormattedValue(item, selectedHeaderKey)}
//                         </Typography>
//                     </TableCell>
//                 </TableRow>
//             ))}
//         </>
//     );
// }

function MobileResponsiveBodyAmcInves({
    data,
    maskedData = false,
    
}: {
    data: any[]
    maskedData?: boolean
    
}): JSX.Element {
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);
    const theme = useTheme();

    function calculatePercentage(x: string | number, y: string | number): string {
        x = Number(x);
        y = Number(y);

        if (!Number.isFinite(x) || !Number.isFinite(y) || y === 0) {
            return "-";
        }

        return `${Math.floor((x / y) * 100)}%`;
    }

    const getFormattedValue = (item: any, key: string) => {
        switch (key) {
            case 'holding':
                return maskedData ? '****' : (item?.holding !== null && item?.holding !== undefined ? `${(item?.holding).toFixed(2)}%` : "-");
            case 'equity':
                return maskedData ? '****' : (item?.equity !== null && item?.equity !== undefined ? `${(item?.equity).toFixed(2)}%` : "-");
            case 'debt':
                return maskedData ? '****' : (item?.debt !== null && item?.debt !== undefined ? `${(item?.debt).toFixed(2)}%` : "-");
            case 'hybrid':
                return maskedData ? '****' : (item?.hybrid !== null && item?.hybrid !== undefined ? `${(item?.hybrid).toFixed(2)}%` : "-");
            case 'investedValue':
                return maskedData ? '****' : (item?.investedValue < 0
                    ? `- ${formatCurrency({ value: Math.abs(item?.investedValue), locale, currencyCode })}`
                    : `${formatCurrency({ value: item?.investedValue, locale, currencyCode })}`);
            case 'marketValue':
                return maskedData ? '****' : (item?.marketValue < 0
                    ? `- ${formatCurrency({ value: Math.abs(item?.marketValue), locale, currencyCode })}`
                    : `${formatCurrency({ value: item?.marketValue, locale, currencyCode })}`);
            case 'unRelease': {
                if (maskedData) return '****';
                const unRealisedGainLossPerc = calculatePercentage(item?.unRelease, item?.investedValue);
                return `${item.unRelease < 0 ? "-" : ""}${formatCurrency({
                    value: Math.abs(item.unRelease),
                    locale,
                    currencyCode,
                })} (${unRealisedGainLossPerc})`;
            }
            case 'release': {
                if (maskedData) return '****';
                const realizedGainLossPerc = calculatePercentage(item?.release, item?.investedValue);
                return `${item.release < 0 ? "-" : ""}${formatCurrency({
                    value: Math.abs(item.release),
                    locale,
                    currencyCode,
                })} (${realizedGainLossPerc})`;
            }
            case 'dividend':
                return maskedData ? '****' : (item?.dividend != null ? `${item.dividend.toFixed(2)}%` : '0%');
            case 'irr':
                return maskedData ? '****' : (item?.irr != null ? `${item.irr.toFixed(2)}%` : '0%');
            default:
                return item?.[key] ?? "-";
        }
    };

  

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {data.map((item, index) => (
                <Box
                    key={`${item?.amcname ?? ""}-${index}`}
                    sx={(theme: Theme) => ({
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: "12px",
                        padding: "16px",
                        backgroundColor: theme.palette.background.paper,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                            borderColor: theme.palette.primary[500],
                            boxShadow: theme.shadows[2],
                        },
                    })}
                >
                    {/* AMC Name */}
                    <Typography
                        variant="body_600"
                        sx={(theme: Theme) => ({
                            color: theme.palette.text.primary,
                            fontSize: "16px",
                            marginBottom: "8px",
                        })}
                    >
                        {item?.amcname ?? "-"}
                    </Typography>

                    {/* AMC Details - Left/Right Alignment */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                        }}
                    >
                        {InvestorLevelAmctableHeaderData
                            .filter(header => header.id !== "amcname") // AMC name shown above
                            .map(header => (
                                <Box
                                    key={header.id}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography
                                        variant="overline"
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.text.secondary,
                                            fontSize: "12px",
                                            lineHeight: "16px",
                                            textAlign: "left"
                                        })}
                                    >
                                        {header.label}
                                    </Typography>
                                    <Typography
                                        variant="body_500"
                                        sx={(theme: Theme) => ({
                                            color: getValueColor(header.id, item?.[header.id], theme, maskedData),
                                            fontSize: "14px",
                                            lineHeight: "20px",
                                            textAlign: "right"
                                        })}
                                    >
                                        {getFormattedValue(item, header.id)}
                                    </Typography>
                                </Box>
                            ))}
                    </Box>
                </Box>
            ))}
        </Box>
    );
}